<script setup lang="ts">
	import type { lineTabContext } from '@/types'

	type statusClassContext = {
		default: ClassContext
		color: ClassContext
		font: ClassContext
	}
	type TabClassContext = {
		default: ClassContext
		paddingBlock: ClassContext
		paddingInline: ClassContext
		width: ClassContext
		base: ClassContext
		border: ClassContext
		transition: ClassContext
		margin: ClassContext
		alignItems: ClassContext
		rounded: ClassContext
		active: Partial<statusClassContext>
		inactive: Partial<statusClassContext>
	}
	type WrapperClassContext = {
		default: ClassContext
		height: ClassContext
		width: ClassContext
		display: ClassContext
		alignItems: ClassContext
		shadow: ClassContext
		bg: ClassContext
		margin: ClassContext
		rounded: ClassContext
	}
	type RootClass = {
		wrapper: WrapperClassContext
		tab: TabClassContext
	}

	const props = withDefaults(
		defineProps<{
			bodyClass?: string | string[] | [] | object[]
			contentClass?: string | string[] | [] | object[]
			tabs: Array<lineTabContext>
			activeTabClass?: string
			activeTab?: string | number
			useDefaultWrapperClass?: boolean
			tabClass?: string
			btnWrapper?: string
			loading?: boolean
			useUrl?: boolean
			queryKey?: string
			ui?: Partial<{
				wrapper: Partial<WrapperClassContext>
				tab: Partial<TabClassContext>
			}>
		}>(),
		{
			queryKey: 'tab',
			bodyClass: 'pt-5',
			useDefaultWrapperClass: true
		}
	)
	const { tab, wrapper }: RootClass = {
		wrapper: {
			default: 'whitespace-nowrap',
			height: 'min-h-[38px]',
			width: 'w-auto',
			display: 'flex flex-none',
			alignItems: 'items-center',
			shadow: 'shadow-sm',
			bg: 'bg-white',
			margin: 'mx-auto',
			rounded: 'rounded-x-[8px]'
		},
		tab: {
			default: props.tabClass,
			paddingBlock: 'py-4',
			paddingInline: 'px-5',
			width: 'min-w-[100px]  md:min-w-[155px]',
			base: 'default-btn',
			border: '!border-0',
			transition: 'transition  ease-linear',
			margin: '!m-0',
			alignItems: 'justify-center',
			rounded: 'rounded-[8px]',
			active: {
				default: '',
				color: 'bg-[#ECFDF3] text-primary-2',
				font: 'font-[600]'
			},
			inactive: {
				default: '',
				color: 'text-gray-500',
				font: 'font-medium'
			}
		} as TabClassContext
	}
	const selectedTab = ref()
	const records = ref()
	const { urlQuery } = useUtils()
	const { replace } = useRouter()

	const emits = defineEmits<{
		(arg: 'change', ...param: unknown[]): void
		(arg: 'update:activeTab', param: string | number): void
	}>()
	const tabList = ref<Array<lineTabContext>>([])
	const selectedTabContent = computed(() => {
		return tabList.value.find(t => t.key === selectedTab.value)
	})
	const selectTab = (e: string, content?: RecordSchema | null, preventEmit = false) => {
		const { toLower } = useUtils()
		selectedTab.value = e
		tabList.value.forEach(item => (item.active = toLower(item.key) === toLower(e)))
		records.value = content || tabList.value.find(item => toLower(item.key) === toLower(e) || item.active)

		if (!preventEmit) {
			if (props.useUrl) {
				replace({
					query: {
						...urlQuery.value,
						[props.queryKey]: e
					}
				})
			}
			emits('update:activeTab', selectedTab.value)
			emits('change', selectedTab.value, content)
		}
	}

	const mountTab = (preventEmit = true) => {
		const queryKey = String(urlQuery.value?.[props?.queryKey] ?? '')
		const firstKey = String(tabList.value[0]?.key ?? '')
		// check for tab key in the url query parameter
		if (queryKey !== undefined && queryKey !== '') {
			selectedTab.value = tabList.value.find(item => item.key === queryKey)?.key ?? firstKey ?? ''
		} else if (!props.activeTab) {
			selectedTab.value = firstKey || ''
		} else {
			const paramKey = tabList.value.find(item => item?.active)?.key || props.activeTab
			selectedTab.value = paramKey
		}

		selectTab(selectedTab.value, null, preventEmit)
	}

	watch(
		() => props.tabs,
		list => {
			tabList.value = list.filter(item => !item.disabled)
			// Track For Changes
			mountTab()
		},
		{ immediate: true }
	)
</script>

<template>
	<div>
		<div class="flex flex-1 overflow-hidden overflow-x-auto">
			<div
				:class="[
					ui?.wrapper?.default ?? wrapper.default,
					ui?.wrapper?.rounded ?? wrapper.rounded,
					ui?.wrapper?.width ?? wrapper.width,
					ui?.wrapper?.alignItems ?? wrapper.alignItems,
					ui?.wrapper?.bg ?? wrapper.bg,
					ui?.wrapper?.display ?? wrapper.display,
					ui?.wrapper?.height ?? wrapper.height,
					ui?.wrapper?.margin ?? wrapper.margin,
					ui?.wrapper?.shadow ?? wrapper.shadow
				]"
			>
				<template v-for="(item, i) in tabList" :key="item.key">
					<button
						v-if="!item.disabled"
						:id="String(item.key)"
						type="button"
						:class="[
							ui?.tab?.default ?? tab.default,
							ui?.tab?.alignItems ?? tab?.alignItems,
							ui?.tab?.base ?? tab?.base,
							ui?.tab?.border ?? tab?.border,
							ui?.tab?.default ?? tab?.default,
							ui?.tab?.margin ?? tab?.margin,
							ui?.tab?.paddingBlock ?? tab?.paddingBlock,
							ui?.tab?.paddingInline ?? tab?.paddingInline,
							ui?.tab?.rounded ?? tab?.rounded,
							ui?.tab?.transition ?? tab?.transition,
							ui?.tab?.width ?? tab?.width,
							item.key === selectedTab || item.active
								? [
										activeTabClass,
										ui?.tab?.active?.default ?? tab.active.default,
										ui?.tab?.active?.color ?? tab.active?.color,
										ui?.tab?.active?.font ?? tab.active?.font
									]
								: [
										ui?.tab?.inactive?.default ?? tab.inactive.default,
										ui?.tab?.inactive?.color ?? tab.inactive?.color,
										ui?.tab?.inactive?.font ?? tab.inactive?.font
									]
						]"
						:data-testid="item.key"
						:name="String(item.key)"
						@click="selectTab(String(item.key), item)"
					>
						<slot name="keys" :content="item" :index="i">
							<slot :name="`key(${item.key})`" :content="item" :index="i">
								{{ item.name }}
							</slot>
						</slot>
					</button>
				</template>
			</div>
		</div>

		<div class="tabs-wrapper flex-col flex-wrap overflow-hidden overflow-x-auto" :class="bodyClass">
			<div class="tab-content" :class="contentClass">
				<slot name="body" :content="selectedTabContent" :record="records">
					<slot :name="`body(${selectedTab})`" :content="selectedTabContent" :record="records" />
					<slot :key="selectedTab" :content="selectedTabContent" :record="records" />
				</slot>
			</div>
		</div>
	</div>
</template>
